import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { map, Observable } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { CustomersFiltersComponent } from 'src/app/features/customers/components/customers-filters/customers-filters.component';
import { LocalStorageService } from 'src/app/shared/localstorage.service';
import { parseCatalogsFilterRequest, refillCatalogsFilter } from 'src/app/shared/utils/helpers';
import { ICatalogFilter } from '../../models/catalogs.models';
import { FetchAllCatalogs } from '../../store/catalogs.actions';
import { IBrand } from 'src/app/features/brands/models/brands.models';
import { ICategory } from 'src/app/features/categories/models/categories.models';
import { ITag } from 'src/app/features/tags/models/tags.models';
import { ILocation } from 'src/app/features/customers/models/customers.models';
import { FetchAllLocations } from 'src/app/features/customers/store/customers.actions';
import { FetchAllBrands } from 'src/app/features/brands/store/brands.actions';
import { FetchAllCategories } from 'src/app/features/categories/store/categories.actions';
import { FetchAllTags } from 'src/app/features/tags/store/tags.actions';
import { FetchCompaniesGroups } from 'src/app/features/companies-groups/store/companies-groups.actions';
import { ICompaniesGroups } from 'src/app/features/companies-groups/models/companies-groups.model';
import { FormControl, Validators } from '@angular/forms';

@Component({
  selector: 'app-catalogs-filter',
  templateUrl: './catalogs-filter.component.html',
  styleUrls: ['./catalogs-filter.component.scss']
})
export class CatalogsFilterComponent implements OnInit {
  readonly separatorKeysCodes = [ENTER, COMMA] as const;

  currentFilter: Observable<ICatalogFilter> = this.store.select(
    (state) => state.catalogs.catalogsFilterState.advanced
  );
  catalogsFilters = LocalStorageService.get('catalogsFilters');

  selectedGroup = 'basic_info';

  filter: ICatalogFilter = refillCatalogsFilter()
  filterName = '';
  toggleSaveFilterForm = false;
  filterNameControl = new FormControl('', Validators.required);

  brands$: Observable<IBrand[]> = this.store.select(
    (state) => [...state.brands.allBrands].sort((a, b) => a.name.localeCompare(b.name))
  );
  filteredBrands$: Observable<IBrand[]> = this.brands$;
  brandsFilterKey: string = '';

  categories$ = this.store.select((state) => [...state.categories.allCategories].sort((a, b) => a.name.localeCompare(b.name)));
  filteredCategories$ = this.categories$;
  categoriesFilterKey: string = '';

  tags$ = this.store.select((state) => [...state.tags.allTags].sort((a, b) => a.name.localeCompare(b.name)));
  filteredTags$ = this.tags$;
  tagsFilterKey: string = '';

  locations$ = this.store.select((state) => [...state.customers.allLocations].sort((a, b) => a.name.localeCompare(b.name)));
  filterlocations$ = this.locations$;
  locationFilterKey: string = '';

  clientGroups$ = this.store.select((state) => [...state.companiesGroups.AllCompaniesGroups].sort((a, b) => a.name.localeCompare(b.name)));
  filteredClientGroups$ = this.clientGroups$;
  clientGroupFilterKey: string = '';

  constructor(
    public dialogRef: MatDialogRef<CustomersFiltersComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private store: Store<AppState>
  ) { }

  ngOnInit(): void {
    this.currentFilter.subscribe((filter: ICatalogFilter) => {
      this.filter = refillCatalogsFilter(filter);
    });

    this.store.dispatch(FetchAllLocations());
    this.store.dispatch(FetchAllCatalogs());
    this.store.dispatch(FetchAllBrands({}));
    this.store.dispatch(FetchAllCategories({}));
    this.store.dispatch(FetchAllTags({}));
    this.store.dispatch(FetchCompaniesGroups({}));
  }

  getFiltersCount(): number {
    return parseCatalogsFilterRequest(this.filter)?.filters?.length || 0;
  }

  close(): void {
    this.dialogRef.close();
  }

  clearFilters(): void {
    this.filter = refillCatalogsFilter();
    this.filterName = '';
  }

  filterBrands(): void {
    this.filteredBrands$ = this.brands$.pipe(
      map((brands) =>
        brands.filter(
          (brand) =>
            brand.name
              .toLocaleLowerCase()
              ?.includes(this.brandsFilterKey.toLocaleLowerCase()!) &&
            !this.filter?.productsFilter?.brands?.find(
              (b) => b.id === brand.id
            )
        )
      )
    );
  }

  filterCategories(): void {
    this.filteredCategories$ = this.categories$.pipe(
      map((categories) =>
        categories.filter(
          (category) =>
            category.name
              .toLocaleLowerCase()
              ?.includes(this.categoriesFilterKey.toLocaleLowerCase()!) &&
            !this.filter?.productsFilter?.categories?.find(
              (c) => c.id === category.id
            )
        )
      )
    );
  }

  filterTags(): void {
    this.filteredTags$ = this.tags$.pipe(
      map((tags) =>
        tags.filter(
          (tag) =>
            tag.name
              .toLocaleLowerCase()
              ?.includes(this.tagsFilterKey.toLocaleLowerCase()!) &&
            !this.filter?.productsFilter?.tags?.find((t) => t.id === tag.id)
        )
      )
    );
  }

  filterLocations(): void {
    this.filterlocations$ = this.locations$.pipe(
      map((locations) =>
        locations.filter(
          (location) =>
            location.name
              .toLocaleLowerCase()
              ?.includes(this.locationFilterKey.toLocaleLowerCase()!) &&
            !this.filter?.locationsFilter?.locations?.find(
              (l) => l.id === location.id
            )
        )
      )
    );
  }

  filterClientGroups(): void {
    this.filteredClientGroups$ = this.clientGroups$.pipe(
      map((groups) =>
        groups.filter(
          (group) =>
            group.name
              .toLocaleLowerCase()
              ?.includes(this.clientGroupFilterKey.toLocaleLowerCase()!) &&
            !this.filter?.locationsFilter?.client_groups?.find(
              (g) => g.id === group.id
            )
        )
      )
    );
  }

  selectBrand(brand: IBrand): void {
    if (this.filter?.productsFilter?.brands)
      this.filter?.productsFilter?.brands?.push(brand);
    this.brandsFilterKey = '';
    this.filterBrands();
  }

  selectCategory(category: ICategory): void {
    this.filter?.productsFilter?.categories?.push(category);
    this.categoriesFilterKey = '';
    this.filterCategories();
  }

  selectLocation(location: ILocation): void {
    if (this.filter?.locationsFilter?.locations)
      this.filter?.locationsFilter?.locations?.push(location);
    this.locationFilterKey = '';
    this.filterLocations();
  }

  selectClientGroup(group: ICompaniesGroups): void {
    if (this.filter?.locationsFilter?.client_groups)
      this.filter?.locationsFilter?.client_groups?.push(group);
    this.clientGroupFilterKey = '';
    this.filterClientGroups();
  }

  getLocationsNames(): string {
    return (this.filter?.locationsFilter?.locations || [])
      .map((location) => location.name)
      .join(', ');
  }

  getGroupesNames(): string {
    return (this.filter?.locationsFilter?.client_groups || [])
      .map((group) => group.name)
      .join(', ');
  }

  selectTag(tag: ITag): void {
    this.filter?.productsFilter?.tags?.push(tag);
    this.tagsFilterKey = '';
    this.filterTags();
  }

  getBrandsNames(): string {
    return (this.filter?.productsFilter?.brands || [])
      .map((brand) => brand.name)
      .join(', ');
  }

  getCategoriesNames(): string {
    return (this.filter?.productsFilter?.categories || [])
      .map((category) => category.name)
      .join(', ');
  }

  getTagsNames(): string {
    return (this.filter?.productsFilter?.tags || [])
      .map((tag) => tag.name)
      .join(', ');
  }

  toggleSaveFilter(): void {
    this.toggleSaveFilterForm = !this.toggleSaveFilterForm;
  }

  save(): boolean | void {
    if(!this.filterNameControl.valid){
      this.filterNameControl.markAsTouched();
      return false
    }
    if (!this.catalogsFilters?.filters)
      this.catalogsFilters = {
        filters: [{ name: this.filterName, filter: this.filter }],
      };
    else {
      const filter = this.catalogsFilters.filters?.find(
        (f: any) => f.name.toLowerCase() === this.filterName.toLowerCase()
      );
      if (filter)
        this.catalogsFilters.filters = this.catalogsFilters.filters?.map((f: any) => {
          if (f.name === this.filterName) return { name: this.filterName, filter: this.filter };
          return f;
        });
      else
        this.catalogsFilters.filters.push({
          name: this.filterName,
          filter: this.filter,
        });
    }
    LocalStorageService.put('catalogsFilters', this.catalogsFilters);
    this.toggleSaveFilter();
  }

  setFilter(filter: any): void {
    if(filter){
      this.filterName = filter.name;
      this.filter = refillCatalogsFilter(filter.filter);
    }
  }


  deleteFilter(filter: any): void {
    this.catalogsFilters.filters = this.catalogsFilters.filters?.filter(
      (f: any) => f.name !== filter.name
    );
    LocalStorageService.put('catalogsFilters', this.catalogsFilters);
  }

  apply(): void {
    this.dialogRef.close(this.filter);
  }

}
